/**
 * 安全的表达式求值器
 * 不使用 eval 或 new Function
 */

import type { DataValue } from "../types";

/** 比较运算符列表 */
const COMPARISON_OPERATORS = ["===", "!==", "==", "!=", "<=", ">=", "<", ">"];

/**
 * 安全的表达式求值器
 */
export class ExpressionEvaluator {
  /**
   * 求值表达式
   * @param expr 已解析变量引用后的表达式
   */
  evaluate(expr: string): DataValue {
    return this.safeEvaluate(expr);
  }

  /**
   * 将 DataValue 转换为布尔值
   */
  isTruthy(value: DataValue): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    if (typeof value === "boolean") {
      return value;
    }
    if (typeof value === "number") {
      return value !== 0;
    }
    if (typeof value === "string") {
      return value !== "";
    }
    return true;
  }

  /**
   * 安全的表达式求值（不使用 eval 或 new Function）
   */
  private safeEvaluate(expr: string): DataValue {
    const trimmed = expr.trim();

    // 处理括号表达式
    if (trimmed.startsWith("(") && trimmed.endsWith(")")) {
      if (this.isMatchedParentheses(trimmed)) {
        return this.safeEvaluate(trimmed.slice(1, -1));
      }
    }

    // 处理 || 运算符
    const orParts = this.splitByOperator(trimmed, "||");
    if (orParts.length > 1) {
      for (const part of orParts) {
        if (this.isTruthy(this.safeEvaluate(part))) {
          return true;
        }
      }
      return false;
    }

    // 处理 && 运算符
    const andParts = this.splitByOperator(trimmed, "&&");
    if (andParts.length > 1) {
      for (const part of andParts) {
        if (!this.isTruthy(this.safeEvaluate(part))) {
          return false;
        }
      }
      return true;
    }

    // 处理 ! 非运算符
    if (trimmed.startsWith("!")) {
      return !this.isTruthy(this.safeEvaluate(trimmed.slice(1)));
    }

    // 处理比较运算符
    for (const op of COMPARISON_OPERATORS) {
      const opIndex = this.findOperatorIndex(trimmed, op);
      if (opIndex !== -1) {
        const left = this.safeEvaluate(trimmed.slice(0, opIndex));
        const right = this.safeEvaluate(trimmed.slice(opIndex + op.length));
        return this.compareValues(left, right, op);
      }
    }

    // 解析基本值
    return this.parseBasicValue(trimmed);
  }

  /**
   * 检查括号是否完整匹配
   */
  private isMatchedParentheses(expr: string): boolean {
    let depth = 0;
    for (let i = 0; i < expr.length; i++) {
      if (expr[i] === "(") {
        depth++;
      }
      if (expr[i] === ")") {
        depth--;
      }
      if (depth === 0 && i < expr.length - 1) {
        return false;
      }
    }
    return true;
  }

  /**
   * 按运算符分割表达式（考虑括号嵌套）
   */
  private splitByOperator(expr: string, operator: string): string[] {
    const parts: string[] = [];
    let current = "";
    let depth = 0;
    let inString: string | null = null;

    for (let i = 0; i < expr.length; i++) {
      const char = expr[i];

      // 处理字符串
      if ((char === '"' || char === "'") && (i === 0 || expr[i - 1] !== "\\")) {
        if (inString === null) {
          inString = char;
        } else if (inString === char) {
          inString = null;
        }
      }

      if (inString === null) {
        if (char === "(") {
          depth++;
        }
        if (char === ")") {
          depth--;
        }

        // 检查运算符
        if (depth === 0 && expr.slice(i, i + operator.length) === operator) {
          parts.push(current);
          current = "";
          i += operator.length - 1;
          continue;
        }
      }

      current += char;
    }

    if (current) {
      parts.push(current);
    }

    return parts;
  }

  /**
   * 查找运算符位置（考虑括号和字符串）
   */
  private findOperatorIndex(expr: string, operator: string): number {
    let depth = 0;
    let inString: string | null = null;

    for (let i = 0; i < expr.length; i++) {
      const char = expr[i];

      if ((char === '"' || char === "'") && (i === 0 || expr[i - 1] !== "\\")) {
        if (inString === null) {
          inString = char;
        } else if (inString === char) {
          inString = null;
        }
      }

      if (inString === null) {
        if (char === "(") {
          depth++;
        }
        if (char === ")") {
          depth--;
        }

        if (depth === 0 && expr.slice(i, i + operator.length) === operator) {
          // 避免匹配 === 中的 == 等情况
          const nextChar = expr[i + operator.length];
          const prevChar = expr[i - 1];
          if (
            operator === "==" &&
            (nextChar === "=" || prevChar === "!" || prevChar === "=")
          ) {
            continue;
          }
          if (operator === "!=" && nextChar === "=") {
            continue;
          }
          if (operator === "<" && nextChar === "=") {
            continue;
          }
          if (operator === ">" && nextChar === "=") {
            continue;
          }
          return i;
        }
      }
    }

    return -1;
  }

  /**
   * 比较两个值
   */
  private compareValues(
    left: DataValue,
    right: DataValue,
    op: string,
  ): boolean {
    switch (op) {
      case "===":
        return left === right;
      case "!==":
        return left !== right;
      case "==":
        return left == right;
      case "!=":
        return left != right;
      case "<":
        return (left as number) < (right as number);
      case ">":
        return (left as number) > (right as number);
      case "<=":
        return (left as number) <= (right as number);
      case ">=":
        return (left as number) >= (right as number);
      default:
        return false;
    }
  }

  /**
   * 解析基本值（字符串、数字、布尔值等）
   */
  private parseBasicValue(value: string): DataValue {
    const trimmed = value.trim();

    // 字符串
    if (
      (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
      (trimmed.startsWith("'") && trimmed.endsWith("'"))
    ) {
      return trimmed.slice(1, -1);
    }

    // 数字
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
      return Number(trimmed);
    }

    // 布尔值
    if (trimmed === "true") {
      return true;
    }
    if (trimmed === "false") {
      return false;
    }

    // null / undefined
    if (trimmed === "null") {
      return null;
    }
    if (trimmed === "undefined") {
      return undefined;
    }

    // 其他情况返回字符串本身
    return trimmed;
  }
}
